import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import { FontFamily, TextStyle } from '@tiptap/extension-text-style';
import { TableKit } from '@tiptap/extension-table';
import { ListItem } from '@tiptap/extension-list';

/**
 * Configures `StarterKit`, an extension that adds most
 * common nodes and marks for the editor (paragraph, heading, bold, italic, etc.)
 */
export const BaseExtension = StarterKit.configure({
  listKeymap: false,
  link: false,
  // Disabled here so it can be re-registered by `ListItemExtension` with a wider content spec.
  listItem: false,
});

/**
 * StarterKit's `ListItem` requires a list item to *start* with a paragraph (`paragraph block*`).
 * Legacy CKEditor 4 content routinely puts a heading first (`<li><h4>..</h4><p>..</p></li>`),
 * which ProseMirror cannot place, so it lifts the whole list item's content out of the list and
 * leaves an empty `<li>` behind. Widening the spec to `block+` keeps that content in the list.
 */
export const ListItemExtension = ListItem.extend({
  content: 'block+',
});

/**
 * Dedicated link extension that's been extended to parse HTML a little differently when hitting the editor.
 * In `parseHTML`, we want to ignore any special parsing rules for img and div tags.
 * This allows an additional LinkBlock extension to handle these cases, and not interfere with the core
 * link extension.
 */
export const LinkExtension = Link.configure({
  openOnClick: false,
  enableClickSelection: true,
  linkOnPaste: true,
  protocols: ['mailto', {
    scheme: 'tel',
    optionalSlashes: true,
  }],
  HTMLAttributes: {
    rel: null,
  },
}).extend({
  parseHTML() {
    return [
      {
        tag: 'a[href]',
        getAttrs: (node) => {
          if (node.firstElementChild && ['IMG', 'DIV', 'P', 'SPAN'].includes(node.firstElementChild.nodeName)) {
            return false;
          }

          return node;
        },
      },
    ];
  },
});

/**
 * Configures TextAlign extension and allows alignment for common nodes.
 */
export const TextAlignExtension = TextAlign.configure({
  defaultAlignment: null,
  types: ['heading', 'paragraph', 'orderedList', 'bulletList', 'blockquote'],
});

/**
 * Standard image extension config.
 */
export const ImageExtension = Image.configure();

/**
 * Standard table extension config.
 */
export const TableKitExtension = TableKit.configure();

/**
 * Standard config for Font Family extension
 */
export const FontFamilyExtension = FontFamily.configure();

/**
 * Standard config for TextStyle extension required by Font Family
 */
export const TextStyleExtension = TextStyle.configure();
